import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import { IoClose } from "react-icons/io5";
import {
  HiOutlineUser,
  HiOutlineEnvelope,
  HiOutlineCamera,
} from "react-icons/hi2";
import { EtatGlobalContext } from "../EtatGlobal.js";
import { authFetch, httpClient } from '../../HttpClient';
import { uploadFileToBlob } from '../../utils/uploadFileToBlob';
import { MODULE_KEYS, getFunctionalityIcon } from "./moduleIcons.js";

const inputClass =
  "w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]";
const labelClass = "text-xs font-medium text-gray-500";

function Create({ onCancel }) {
  const { users, setUsers } = useContext(EtatGlobalContext);
  const [newUser, setNewUser] = useState({
    civilite: "Monsieur",
    name: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    poste: "",
    role: "COLLABORATEUR",
    pole: "COMPTABILITE",
    picture: null,
    fonctionalite: [],
    cabinetId: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState(null);
  const [cabinets, setCabinets] = useState([]);
  const fileInputRef = useRef(null);

  const picturePreviewUrl = useMemo(
    () => (newUser.picture ? URL.createObjectURL(newUser.picture) : null),
    [newUser.picture]
  );

  useEffect(() => {
    return () => {
      if (picturePreviewUrl) URL.revokeObjectURL(picturePreviewUrl);
    };
  }, [picturePreviewUrl]);

  useEffect(() => {
    httpClient
      .get("https://backend-myalfa.vercel.app/api/cabinets")
      .then((res) => setCabinets(res.data))
      .catch((err) => console.error("Erreur chargement cabinets :", err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewUser({
      ...newUser,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    setNewUser({
      ...newUser,
      picture: e.target.files[0],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Upload direct navigateur → Blob (contourne la limite 4,5 Mo), avec
    // une progression affichée pendant l'envoi.
    let pictureUrl = null;
    if (newUser.picture) {
      try {
        pictureUrl = await uploadFileToBlob(
          newUser.picture,
          "https://backend-myalfa.vercel.app/api/users/upload-token",
          ({ percentage }) => setUploadProgress(Math.round(percentage))
        );
      } catch (uploadError) {
        console.error("Erreur upload photo:", uploadError);
        setError("L'envoi de la photo a échoué. Merci de réessayer.");
        setUploadProgress(null);
        setIsLoading(false);
        return;
      }
      setUploadProgress(null);
    }

    const payload = {
      civilite: newUser.civilite,
      name: newUser.name,
      lastName: newUser.lastName,
      email: newUser.email,
      phone: newUser.phone,
      password: newUser.password,
      poste: newUser.poste,
      role: newUser.role,
      pole: newUser.pole,
      pictureUrl,
      fonctionalite: JSON.stringify(newUser.fonctionalite),
    };
    if (newUser.cabinetId) payload.cabinetId = newUser.cabinetId;

    authFetch("https://backend-myalfa.vercel.app/api/users", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        // Vérifier si l'erreur est liée à l'email déjà utilisé
        if (data.message && data.message.includes("email existe déjà")) {
          setError("Cet email existe déjà et ne peut pas être utilisé.");
        } else {
          setUsers([...users, data]);

          // Après création réussie -> Envoyer l'email
          const emailPayload = {
            email: newUser.email,
            login: newUser.email,
            password: newUser.password,
          };

          authFetch("https://backend-myalfa.vercel.app/api/emailLogin", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(emailPayload),
          })
            .then((response) => response.json())
            .then((emailData) => {
              console.log("Email envoyé avec succès :", emailData);
              onCancel(); // Fermer le formulaire après envoi de l'email
            })
            .catch((emailError) => {
              console.error("Erreur lors de l'envoi de l'email :", emailError);
              // Tu peux décider ici de faire un alert ou afficher une erreur
            });
        }
      })

      .catch((error) => {
        console.error("Erreur:", error);
        setError(
          "Une erreur est survenue lors de la création de l'utilisateur."
        );
      })
      .finally(() => setIsLoading(false));
  };

  const handleToggleChange = (item) => {
    setNewUser((prevUser) => {
      let updatedFonctionalite = [...prevUser.fonctionalite];
      if (updatedFonctionalite.includes(item)) {
        updatedFonctionalite = updatedFonctionalite.filter((f) => f !== item);
      } else {
        updatedFonctionalite.push(item);
      }
      return {
        ...prevUser,
        fonctionalite: updatedFonctionalite,
      };
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/45 z-[1000] flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-7 py-6 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">
            Ajouter un collaborateur
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
          >
            <IoClose size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-7 py-6">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="border border-gray-100 rounded-2xl p-5">
              <div className="flex items-center gap-2 pb-3 mb-3 border-b border-gray-100">
                <HiOutlineUser size={16} className="text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900">Identité</h3>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Civilité *</label>
                  <select
                    name="civilite"
                    value={newUser.civilite}
                    onChange={handleChange}
                    required
                    className={inputClass}
                  >
                    <option value="Monsieur">Monsieur</option>
                    <option value="Madame">Madame</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Profil *</label>
                  <select
                    name="role"
                    value={newUser.role}
                    onChange={handleChange}
                    required
                    className={inputClass}
                  >
                    <option value="EXPERT_COMPTABLE">Expert comptable</option>
                    <option value="SUPERVISEUR">Superviseur</option>
                    <option value="COLLABORATEUR">Collaborateur</option>
                    <option value="ASSISTANT">Assistant</option>
                    <option value="ADMINISTRATEUR">Administrateur</option>
                    <option value="SECRETARIAT">Secrétariat</option>
                    <option value="SOCIAL">Social</option>
                    <option value="JURIDIQUE">Juridique</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Pôle * (bureau affiché)</label>
                  <select
                    name="pole"
                    value={newUser.pole}
                    onChange={handleChange}
                    required
                    className={inputClass}
                  >
                    <option value="COMPTABILITE">Comptabilité</option>
                    <option value="SOCIAL">Social</option>
                    <option value="SECRETARIAT">Secrétariat</option>
                    <option value="JURIDIQUE">Juridique</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Nom *</label>
                  <input
                    type="text"
                    name="name"
                    value={newUser.name}
                    onChange={handleChange}
                    required
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Prénom *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={newUser.lastName}
                    onChange={handleChange}
                    required
                    className={inputClass}
                  />
                </div>
                <div className="col-span-2">
                  <label className={labelClass}>Fonction *</label>
                  <input
                    type="text"
                    name="poste"
                    value={newUser.poste}
                    onChange={handleChange}
                    required
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="mt-4 bg-[#fff6ee] rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-600 mb-3">Photo de profil</p>
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl bg-[#5C1A30]/10 flex items-center justify-center text-[#5C1A30] flex-shrink-0 overflow-hidden">
                    {picturePreviewUrl ? (
                      <img
                        src={picturePreviewUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <HiOutlineUser size={22} />
                    )}
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <HiOutlineCamera size={14} />
                      Importer
                    </button>
                    <p className="text-[11px] text-gray-400 mt-1.5">JPG ou PNG · max 2 Mo</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      name="picture"
                      accept="image/png,image/jpeg"
                      hidden
                      onChange={handleFileChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-gray-100 rounded-2xl p-5">
              <div className="flex items-center gap-2 pb-3 mb-3 border-b border-gray-100">
                <HiOutlineEnvelope size={16} className="text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900">Coordonnées</h3>
              </div>

              <div className="flex flex-col gap-3">
                <div>
                  <label className={labelClass}>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={newUser.email}
                    onChange={handleChange}
                    required
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Téléphone</label>
                  <input
                    type="text"
                    name="phone"
                    value={newUser.phone}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Mot de passe *</label>
                  <input
                    type="password"
                    name="password"
                    value={newUser.password}
                    onChange={handleChange}
                    required
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="mt-4 bg-[#fff6ee] rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-600 mb-3">Rattachement</p>
                <label className={labelClass}>Cabinet</label>
                <select
                  name="cabinetId"
                  value={newUser.cabinetId}
                  onChange={handleChange}
                  className={`${inputClass} bg-white`}
                >
                  <option value="">-- Aucun cabinet --</option>
                  {cabinets.map((cab) => (
                    <option key={cab.id} value={cab.id}>
                      {cab.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="border border-gray-100 rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              Droits aux modules
            </h3>
            <div className="grid grid-cols-2 gap-2.5">
              {MODULE_KEYS.map((item) => {
                const active = newUser.fonctionalite.includes(item);
                return (
                  <div
                    key={item}
                    onClick={() => handleToggleChange(item)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-colors ${
                      active ? "bg-[#5C1A30]" : "bg-[#f5ede8]"
                    }`}
                  >
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-black/10">
                      {getFunctionalityIcon(item, active)}
                    </div>
                    <span
                      className={`text-sm font-medium ${
                        active ? "text-white" : "text-gray-400"
                      }`}
                    >
                      {item}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 mt-4">{error}</div>
          )}

          <div className="flex items-center justify-end gap-3 mt-6 pt-5 border-t border-gray-100">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-xl border border-[#840040] text-[#840040] text-sm font-semibold hover:bg-[#840040]/5 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 rounded-xl bg-[#5C1A30] text-white text-sm font-semibold hover:bg-[#4a1526] transition-colors disabled:opacity-60"
            >
              {isLoading
                ? (uploadProgress !== null ? `Envoi... ${uploadProgress}%` : "Ajout en cours...")
                : "Ajouter"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Create;
